import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { INTERVIEW_ROLE_OPTIONS } from "../../utils/interviewRoles";
import "../../styles/components/_dashboard-tour.scss";

// ─── Step definitions ─────────────────────────────────────────────────────────
// `targetSelector` is a CSS selector for the element to spotlight.
// null = no spotlight (welcome / finish screens).
// `placement` controls which side of the target the tooltip appears on.
const STEPS = [
    {
        key: "welcome",
        title: "Welcome to Mooz",
        body: "Take a quick tour of your practice space. We’ll show you where to find questions, recordings, feedback, and everything you need to keep improving.",
        primary: "Show me around",
        secondary: "Skip tour",
        targetSelector: null,
        placement: null,
        showIllustration: true,
    },
    {
        key: "home",
        title: "Home",
        body: "Your practice hub - view recent activity, quick-start a session, and track your progress.",
        primary: "Next",
        secondary: "Back",
        targetSelector: 'a.sidebar__nav-item[href="/dashboard"]',
        placement: "right",
    },
    {
        key: "questions",
        title: "Question bank",
        body: "Browse curated interview questions by category.",
        primary: "Next",
        secondary: "Back",
        targetSelector: 'a.sidebar__nav-item[href="/dashboard/questions"]',
        placement: "right",
    },
    {
        key: "recordings",
        title: "Recordings",
        body: "Rewatch every take, read personalised feedback, and track how your answers improve over time.",
        primary: "Next",
        secondary: "Back",
        targetSelector: 'a.sidebar__nav-item[href="/dashboard/recordings"]',
        placement: "right",
    },
    {
        key: "profile",
        title: "Your profile",
        body: "Update your name, manage your plan, and change your password from this page.",
        primary: "Next",
        secondary: "Back",
        targetSelector: "a.sidebar__profile",
        placement: "right",
    },
    {
        key: "name",
        title: "What's your name?",
        body: "This will appear on your dashboard and in your feedback.",
        primary: "Next",
        secondary: "Back",
        targetSelector: null,
        placement: null,
        isName: true,
    },
    {
        key: "get-started",
        title: "Ready to practise?",
        body: "Tell us the role you’re interviewing for and we’ll guide you to the most relevant questions.",
        primary: "Start practising",
        secondary: "Back",
        targetSelector: null,
        placement: null,
        isGetStarted: true,
    },
];

// Roles shown in the get-started dropdown
const ROLE_OPTIONS = [
    { value: "", label: "Select a role…" },
    ...INTERVIEW_ROLE_OPTIONS,
];

const PAD = 10; // px padding around the spotlight rect
const TIP_GAP = 18; // px gap between spotlight edge and tooltip

// ─── Illustration ─────────────────────────────────────────────────────────────
function Illustration() {
    return (
        <svg
            className="dtour-illustration"
            viewBox="0 0 560 260"
            role="img"
            aria-label="Dashboard overview illustration"
        >
            <defs>
                <linearGradient id="dtour_bg" x1="0" x2="1" y1="0" y2="1">
                    <stop offset="0" stopColor="#1a1146" />
                    <stop offset="1" stopColor="#3b2b8a" />
                </linearGradient>
                <pattern
                    id="dtour_grid"
                    width="28"
                    height="28"
                    patternUnits="userSpaceOnUse"
                >
                    <path
                        d="M28 0H0V28"
                        fill="none"
                        stroke="rgba(255,255,255,.07)"
                        strokeWidth="1"
                    />
                </pattern>
                <filter id="dtour_shadow">
                    <feDropShadow
                        dx="0"
                        dy="8"
                        stdDeviation="14"
                        floodColor="rgba(0,0,0,.45)"
                    />
                </filter>
                <radialGradient id="dtour_orb" cx="50%" cy="0%" r="70%">
                    <stop offset="0" stopColor="rgba(108,82,214,.45)" />
                    <stop offset="1" stopColor="rgba(108,82,214,0)" />
                </radialGradient>
            </defs>

            <rect width="560" height="260" rx="16" fill="url(#dtour_bg)" />
            <rect width="560" height="260" rx="16" fill="url(#dtour_grid)" />
            <rect width="560" height="260" rx="16" fill="url(#dtour_orb)" />

            {/* Sidebar */}
            <rect x="0" y="0" width="70" height="260" fill="rgba(8,9,18,.5)" />
            <rect x="12" y="12" width="38" height="38" rx="10" fill="#5b4fcf" />
            <rect
                x="14"
                y="64"
                width="42"
                height="16"
                rx="8"
                fill="rgba(255,255,255,.18)"
            />
            {[96, 128, 160].map((y) => (
                <rect
                    key={y}
                    x="16"
                    y={y}
                    width="38"
                    height="10"
                    rx="5"
                    fill="rgba(255,255,255,.2)"
                />
            ))}

            {/* Main content */}
            <g filter="url(#dtour_shadow)">
                {/* Header */}
                <rect
                    x="90"
                    y="16"
                    width="220"
                    height="10"
                    rx="5"
                    fill="rgba(255,255,255,.5)"
                />
                <rect
                    x="90"
                    y="34"
                    width="160"
                    height="8"
                    rx="4"
                    fill="rgba(255,255,255,.28)"
                />
                <rect
                    x="408"
                    y="22"
                    width="92"
                    height="18"
                    rx="9"
                    fill="rgba(255,255,255,.2)"
                />

                {/* Metric cards */}
                {[0, 1, 2, 3].map((i) => (
                    <g key={i}>
                        <rect
                            x={90 + i * 114}
                            y="60"
                            width="106"
                            height="58"
                            rx="12"
                            fill="rgba(255,255,255,.1)"
                            stroke="rgba(255,255,255,.14)"
                        />
                        <rect
                            x={100 + i * 114}
                            y="70"
                            width="40"
                            height="8"
                            rx="4"
                            fill="rgba(255,255,255,.45)"
                        />
                        <rect
                            x={100 + i * 114}
                            y="84"
                            width="28"
                            height="18"
                            rx="6"
                            fill="rgba(255,255,255,.3)"
                        />
                    </g>
                ))}

                {/* Activity panel */}
                <rect
                    x="90"
                    y="132"
                    width="450"
                    height="104"
                    rx="14"
                    fill="rgba(255,255,255,.1)"
                    stroke="rgba(255,255,255,.14)"
                />
                <rect
                    x="104"
                    y="146"
                    width="120"
                    height="8"
                    rx="4"
                    fill="rgba(255,255,255,.45)"
                />
                {[0, 1, 2].map((i) => (
                    <g key={`row-${i}`}>
                        <circle
                            cx="110"
                            cy={168 + i * 20}
                            r="4"
                            fill="rgba(255,255,255,.55)"
                        />
                        <rect
                            x="122"
                            y={164 + i * 20}
                            width="160"
                            height="6"
                            rx="3"
                            fill="rgba(255,255,255,.3)"
                        />
                        <rect
                            x="122"
                            y={172 + i * 20}
                            width="100"
                            height="5"
                            rx="2.5"
                            fill="rgba(255,255,255,.18)"
                        />
                    </g>
                ))}
            </g>
        </svg>
    );
}

// ─── Spotlight rect calculator ────────────────────────────────────────────────
function useSpotlightRect(selector, stepIndex) {
    const [rect, setRect] = useState(null);

    const measure = useCallback(() => {
        if (!selector) {
            setRect(null);
            return;
        }
        const el = document.querySelector(selector);
        if (!el) {
            setRect(null);
            return;
        }
        const r = el.getBoundingClientRect();
        setRect({
            top: r.top - PAD,
            left: r.left - PAD,
            width: r.width + PAD * 2,
            height: r.height + PAD * 2,
        });
    }, [selector]);

    useEffect(() => {
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [measure, stepIndex]);

    return rect;
}

// ─── Tooltip position calculator ─────────────────────────────────────────────
// Returns inline style for the tooltip card given the spotlight rect + placement.
// Uses a bottom anchor when the target sits in the lower portion of the viewport
// so the panel never overflows off-screen.
const PANEL_H_EST = 200; // conservative panel height estimate for clamping

function tooltipStyle(rect, placement, panelW = 320, isMobile = false) {
    if (!rect)
        return {
            position: "fixed",
            bottom: "2rem",
            left: "50%",
            transform: "translateX(-50%)",
            width: panelW,
        };

    const vp = { w: window.innerWidth, h: window.innerHeight };
    const mobileWidth = Math.min(panelW, vp.w - 32);

    if (isMobile) {
        const left = 16;
        const width = mobileWidth;
        const spaceAbove = rect.top - TIP_GAP;
        const spaceBelow = vp.h - (rect.top + rect.height) - TIP_GAP;
        const useBelow = spaceBelow >= PANEL_H_EST || spaceBelow >= spaceAbove;

        if (useBelow) {
            const top = Math.min(
                rect.top + rect.height + TIP_GAP,
                vp.h - PANEL_H_EST - 16
            );
            return { position: "fixed", top, left, width };
        }

        const top = Math.max(rect.top - PANEL_H_EST - TIP_GAP, 16);
        return { position: "fixed", top, left, width };
    }

    if (placement === "right") {
        const left = Math.min(
            rect.left + rect.width + TIP_GAP,
            vp.w - panelW - 16
        );

        // If the target is in the lower 40% of the screen, anchor the tooltip
        // from the bottom so it grows upward and never overflows below.
        if (rect.top + rect.height / 2 > vp.h * 0.6) {
            const bottom = Math.max(vp.h - rect.top + PAD, 16);
            return { position: "fixed", bottom, left, width: panelW };
        }

        // Otherwise vertically centre on the target and clamp to viewport.
        let top = rect.top + rect.height / 2 - PANEL_H_EST / 2;
        top = Math.max(top, 16);
        top = Math.min(top, vp.h - PANEL_H_EST - 16);
        return { position: "fixed", top, left, width: panelW };
    }

    if (placement === "bottom") {
        const top = rect.top + rect.height + TIP_GAP;
        const left = Math.max(
            Math.min(
                rect.left + rect.width / 2 - panelW / 2,
                vp.w - panelW - 16
            ),
            16
        );
        return { position: "fixed", top, left, width: panelW };
    }

    // Fallback: centred bottom
    return {
        position: "fixed",
        bottom: "2rem",
        left: "50%",
        transform: "translateX(-50%)",
        width: panelW,
    };
}

function useIsMobile(bp = 576) {
    const [isMobile, setIsMobile] = useState(() =>
        typeof window === "undefined" ? false : window.innerWidth <= bp
    );

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= bp);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [bp]);

    return isMobile;
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function DashboardTour({
    open,
    onFinish,
    onSaveProfile,
    initialFirstName = "",
    initialLastName = "",
}) {
    const [stepIndex, setStepIndex] = useState(0);
    const [selectedRole, setSelectedRole] = useState("");
    const [rolePickerOpen, setRolePickerOpen] = useState(false);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState("");
    const navigate = useNavigate();
    const primaryRef = useRef(null);
    const firstNameRef = useRef(null);
    const isMobile = useIsMobile();

    const step = STEPS[stepIndex];
    const isFirst = stepIndex === 0;
    const isLast = stepIndex === STEPS.length - 1;
    const isAlmostThereStep =
        step.isGetStarted || stepIndex === STEPS.length - 2;
    const mandatoryStartIndex = useMemo(() => {
        const idx = STEPS.findIndex((s) => s.isName);
        return idx >= 0 ? idx : 0;
    }, []);
    const spotRect = useSpotlightRect(step.targetSelector, stepIndex);
    const tipStyle = tooltipStyle(spotRect, step.placement, 320, isMobile);
    const hasSPotlight = Boolean(spotRect);

    const jumpToMandatorySteps = useCallback(() => {
        setStepIndex(mandatoryStartIndex);
    }, [mandatoryStartIndex]);

    // Reset on open
    useEffect(() => {
        if (!open) return;
        setStepIndex(0);
        setSelectedRole("");
        setRolePickerOpen(false);
        setSaving(false);
        setSaveError("");
    }, [open]);

    useEffect(() => {
        if (!open) return;
        setFirstName((v) => v || initialFirstName || "");
        setLastName((v) => v || initialLastName || "");
    }, [open, initialFirstName, initialLastName]);

    // Focus primary button when step changes
    useEffect(() => {
        if (!open) return;
        const t = setTimeout(() => {
            if (step.isName) {
                firstNameRef.current?.focus();
                return;
            }
            primaryRef.current?.focus();
        }, 60);
        return () => clearTimeout(t);
    }, [open, stepIndex]);

    // Escape to skip
    useEffect(() => {
        if (!open) return;
        const h = (e) => {
            if (e.key !== "Escape") return;
            // Name + role collection are mandatory; route users to setup steps.
            e.preventDefault?.();
            jumpToMandatorySteps();
        };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, [jumpToMandatorySteps, open]);

    // Lock scroll
    useEffect(() => {
        if (!open) return;
        document.body.classList.add("dtour-open");
        return () => document.body.classList.remove("dtour-open");
    }, [open]);

    useEffect(() => {
        if (!rolePickerOpen) return;
        const close = (e) => {
            if (e.target.closest && e.target.closest(".dtour__role-picker"))
                return;
            setRolePickerOpen(false);
        };
        window.addEventListener("mousedown", close);
        return () => window.removeEventListener("mousedown", close);
    }, [rolePickerOpen]);

    if (!open) return null;

    const canStart = Boolean(selectedRole) && !saving;
    const canSaveName =
        firstName.trim().length > 0 && lastName.trim().length > 0 && !saving;
    const primaryDisabled = step.isName
        ? !canSaveName
        : step.isGetStarted
          ? !canStart
          : false;

    const goNext = async () => {
        if (isFirst) return setStepIndex(1);
        if (step.isName) {
            if (!canSaveName) return;
            const payload = {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
            };
            setSaving(true);
            setSaveError("");
            try {
                const ok = await onSaveProfile?.(payload);
                if (ok === false) throw new Error("Profile save failed");
                setStepIndex((i) => i + 1);
            } catch {
                setSaveError("Couldn't save your name. Please try again.");
            } finally {
                setSaving(false);
            }
            return;
        }
        if (step.isGetStarted) {
            if (!selectedRole) return;
            onFinish?.({ completed: true, role: selectedRole });
            navigate("/dashboard/questions");
            return;
        }
        if (isLast) return onFinish?.({ completed: true });
        setStepIndex((i) => i + 1);
    };
    const goBack = () => {
        if (isFirst) {
            const nameIdx = STEPS.findIndex((s) => s.isName);
            return setStepIndex(nameIdx >= 0 ? nameIdx : STEPS.length - 1);
        }
        setStepIndex((i) => i - 1);
    };

    // SVG spotlight overlay path — full screen rect with a punched-out hole
    const svgSpotlight = hasSPotlight
        ? `
        M0 0 H${window.innerWidth} V${window.innerHeight} H0 Z
        M${spotRect.left + 8} ${spotRect.top}
        a8 8 0 0 1 8 -8
        H${spotRect.left + spotRect.width - 8}
        a8 8 0 0 1 8 8
        V${spotRect.top + spotRect.height - 8}
        a8 8 0 0 1 -8 8
        H${spotRect.left + 8}
        a8 8 0 0 1 -8 -8
        Z
    `
        : null;

    return createPortal(
        <div
            className={`dtour${hasSPotlight ? " dtour--spotlight" : ""}`}
            role="dialog"
            aria-modal="true"
            aria-label={`Dashboard tour — ${step.title}`}
            onMouseDown={(e) => {
                if (e.target !== e.currentTarget) return;
                // Name + role collection are mandatory; route users to setup steps.
                jumpToMandatorySteps();
            }}
        >
            {/* SVG overlay with cut-out spotlight */}
            {hasSPotlight && svgSpotlight ? (
                <svg className="dtour__overlay" aria-hidden="true">
                    <path
                        d={svgSpotlight}
                        fillRule="evenodd"
                        className="dtour__overlay-path"
                    />
                    {/* Pulsing ring around spotlight */}
                    <rect
                        className="dtour__spotlight-ring"
                        x={spotRect.left - 1}
                        y={spotRect.top - 1}
                        width={spotRect.width + 1}
                        height={spotRect.height + 1}
                        rx="12"
                    />
                </svg>
            ) : (
                // Plain dark backdrop for welcome / finish screens
                <div className="dtour__backdrop" aria-hidden="true" />
            )}

            {/* Tooltip / panel */}
            <div
                className={`dtour__panel${step.showIllustration ? " dtour__panel--welcome" : ""}${step.isName ? " dtour__panel--wide" : ""}${step.isGetStarted ? " dtour__panel--overflow" : ""}`}
                style={hasSPotlight ? tipStyle : undefined}
                role="document"
            >
                {step.showIllustration && (
                    <div className="dtour__media">
                        <Illustration />
                    </div>
                )}

                <div className="dtour__content">
                    {/* Step counter on non-welcome steps */}
                    {!isFirst && !isAlmostThereStep && (
                        <span className="dtour__step-label" aria-hidden="true">
                            {stepIndex} / {STEPS.length - 1}
                        </span>
                    )}
                    {!isFirst && isAlmostThereStep && (
                        <span className="dtour__step-label" aria-hidden="true">
                            Almost there
                        </span>
                    )}
                    <h2 className="dtour__title">{step.title}</h2>
                    <p className="dtour__body">{step.body}</p>

                    {step.isName && (
                        <form
                            className="dtour__form"
                            onSubmit={(e) => {
                                e.preventDefault();
                                goNext();
                            }}
                        >
                            <div className="dtour__row">
                                <label className="dtour__field">
                                    <span className="dtour__label">
                                        First name
                                    </span>
                                    <input
                                        ref={firstNameRef}
                                        className="dtour__input"
                                        type="text"
                                        autoComplete="given-name"
                                        value={firstName}
                                        onChange={(e) =>
                                            setFirstName(e.target.value)
                                        }
                                        disabled={saving}
                                        required
                                    />
                                </label>
                                <label className="dtour__field">
                                    <span className="dtour__label">
                                        Last name
                                    </span>
                                    <input
                                        className="dtour__input"
                                        type="text"
                                        autoComplete="family-name"
                                        value={lastName}
                                        onChange={(e) =>
                                            setLastName(e.target.value)
                                        }
                                        disabled={saving}
                                        required
                                    />
                                </label>
                            </div>
                            {saveError && (
                                <div className="dtour__error" role="alert">
                                    {saveError}
                                </div>
                            )}
                        </form>
                    )}

                    {/* Role picker — shown only on the get-started step */}
                    {step.isGetStarted && (
                        <div className="dtour__role-wrap">
                            <span className="dtour__role-label">
                                I am interviewing for…
                            </span>

                            <div className="dtour__role-picker">
                                <button
                                    type="button"
                                    className="dtour__role-trigger"
                                    onClick={() => setRolePickerOpen((v) => !v)}
                                    aria-haspopup="listbox"
                                    aria-expanded={rolePickerOpen}
                                >
                                    {selectedRole
                                        ? ROLE_OPTIONS.find(
                                              (o) => o.value === selectedRole
                                          )?.label
                                        : "Select a role…"}
                                    <span aria-hidden="true">▾</span>
                                </button>

                                {rolePickerOpen && (
                                    <div
                                        className="dtour__role-panel"
                                        role="listbox"
                                        aria-label="Select a role"
                                    >
                                        {ROLE_OPTIONS.filter(
                                            (o) => o.value !== ""
                                        ).map((o) => (
                                            <button
                                                key={o.value}
                                                type="button"
                                                role="option"
                                                aria-selected={
                                                    selectedRole === o.value
                                                }
                                                className={`dtour__role-option${
                                                    selectedRole === o.value
                                                        ? " is-selected"
                                                        : ""
                                                }`}
                                                onClick={() => {
                                                    setSelectedRole(o.value);
                                                    setRolePickerOpen(false);
                                                }}
                                            >
                                                {o.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {step.isGetStarted ? (
                    <div className="dtour__actions dtour__actions--stacked">
                        <button
                            ref={primaryRef}
                            type="button"
                            className="dtour__btn dtour__btn--start"
                            onClick={goNext}
                            disabled={!canStart}
                        >
                            {step.primary}
                            {/* <span
                                className="dtour__btn-arrow"
                                aria-hidden="true"
                            >
                                →
                            </span> */}
                        </button>
                        <button
                            type="button"
                            className="dtour__btn dtour__btn--text"
                            onClick={goBack}
                        >
                            {step.secondary}
                        </button>
                    </div>
                ) : (
                    <div className="dtour__actions">
                        <button
                            type="button"
                            className="dtour__btn dtour__btn--ghost"
                            onClick={goBack}
                        >
                            {step.secondary}
                        </button>

                        {/* Progress dots between buttons on non-welcome steps */}
                        {!isFirst && (
                            <div className="dtour__progress" aria-hidden="true">
                                {STEPS.slice(1).map((_, i) => (
                                    <span
                                        key={i}
                                        className={`dtour__dot${i + 1 === stepIndex ? " is-active" : ""}`}
                                    />
                                ))}
                            </div>
                        )}

                        <button
                            ref={primaryRef}
                            type="button"
                            className="dtour__btn dtour__btn--primary"
                            onClick={goNext}
                            disabled={primaryDisabled}
                        >
                            {saving && step.isName ? "Saving…" : step.primary}
                        </button>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}
